import type { ObjectValues } from './types.ts';
import type { LocalityData, LocalitySuggestionData } from 'service/locality/model.ts';

export const DESKTOP_MIN_WIDTH_BREAKPOINT = 900; // this is also defined inside tailwind.config.mjs as screens.md property, so change it there also
export const MOBILE_HEADER_HEIGHT = 122; // this is also defined inside tailwind.config.mjs, so change it there also
export const DESKTOP_HEADER_HEIGHT = 56; // this is also defined inside tailwind.config.mjs, so change it there also
export const DESKTOP_HP_LOGO_POSITION_FROM_TOP = DESKTOP_HEADER_HEIGHT + 20;
export const DESKTOP_MIN_WIDTH_TRANSPARENT_HEADER_BREAKPOINT = 1190;
export const TAB_OFFERS_PER_PAGE = 12;
export const SIMILAR_OFFERS_PER_PAGE = 11; // Leave space for an ad
export const OFFERS_PER_PAGE = 60;
export const ADS_PER_PAGE = 2;
export const UNPACKED_OFFERS_MODIFIER = 5;
export const UNPACKED_OFFERS_PER_PAGE = OFFERS_PER_PAGE / UNPACKED_OFFERS_MODIFIER;
export const OFFERS_WITHOUT_ADS_PER_PAGE = OFFERS_PER_PAGE - ADS_PER_PAGE;
export const OFFERS_WITHOUT_UNPACKED_PER_PAGE = OFFERS_WITHOUT_ADS_PER_PAGE - UNPACKED_OFFERS_PER_PAGE;
export const MAX_OFFERS_OFFSET = 10_000; // Hard limit on BE (due to elastic?)
export const MAX_OFFERS_PAGE = Math.floor(MAX_OFFERS_OFFSET / OFFERS_WITHOUT_ADS_PER_PAGE);
export const MAX_UNPACKED_OFFERS_PAGE = Math.floor(MAX_OFFERS_OFFSET / OFFERS_WITHOUT_UNPACKED_PER_PAGE);
export const CATEGORY_ALL_CATEGORIES_SEO_NAME = '0-vsechny-kategorie';
export const ANALYTICS_TIMEOUT_DELAY = 1_000;
/**
 * Active offers upcoming expiring days.
 * Same value is used in BE.
 */
export const UPCOMING_EXPIRING_DAYS = 5;
export const UPCOMING_EXPIRING_URL_VALUE = 1;

export const OFFER_LIST_CONSTANTS = {
	OFFERS_MIN_COUNT: 13, //minimalni pocet inzeratu pro vydani reklamy
	AD_MIN_COUNT_POS: 4, //pozice reklamy pri minimalnim poctu inzeratu

	OFFERS_MAX_COUNT: 43, //maximalni pocet inzeratu pro vydani reklamy
	AD_MAX_COUNT_POS: 34, // pozice reklamní pozice při maximální počtu inzerátů
	ABTEST_BANNER_AD_MAX_COUNT_POS: 20, // pozice mobilní banner reklamní pozice při maximální počtu inzerátů

	OFFERS_POPULAR_COUNT: 22, //pocet inzeratu pro popularni inzeraty
} as const;

/**
 * Použitelné filtry SDNky
 * @const
 */
export const SDN_FILTER = {
	BASE: '?fl=', // Parametr URL pro filtry u SDNky
	WATERMARK: 'wrm,/watermark/sbazar.png,10,10',
	RESOLUTION: 'res', // Nastavení rozlišení
	JPG_SETTING: 'jpg,80,,1', // Nastavení jpg (kvalita 80%, progresivní)
	WEBP_SETTINGS: 'webp,75', // Nastavení webp (kvalita 75%)
	EXIF: 'exf', // Otočení dle EXIFu
	CROP_BY_RATIO: 'crr,1.33333,2', // Ořez obrázku na daný poměr stran (jen na šířku)
} as const;
export type SDN_FILTER = ObjectValues<typeof SDN_FILTER>;

type sdnParams = {
	width: string; // Šířka obrázku
	height: string; // Výška obrázku
	aspectRatio?: string; // Zobrazit watermark
	isWatermark?: boolean;
	isCropped?: boolean; // Oříznout na základě poměru stran
	isWebP?: boolean;
};

/**
 * Vrátí správnou podobu parametrů pro stažení obrázku z SDNky.
 */
const _getSdnParams = ({
	width,
	height,
	aspectRatio = '1',
	isWatermark = true,
	isCropped = false,
	isWebP = false,
}: sdnParams): string => {
	const prms = [];
	prms.push(SDN_FILTER.EXIF);

	if (isCropped) {
		prms.push(SDN_FILTER.CROP_BY_RATIO);
	}
	prms.push(`${SDN_FILTER.RESOLUTION},${width},${height},${aspectRatio}`);
	if (isWatermark) {
		prms.push(SDN_FILTER.WATERMARK);
	}
	prms.push(isWebP ? SDN_FILTER.WEBP_SETTINGS : SDN_FILTER.JPG_SETTING);

	return `${SDN_FILTER.BASE}${prms.join('|')}`;
};

export const SDN = {
	w200: _getSdnParams({
		width: '200',
		height: '150',
		aspectRatio: '3',
		isWatermark: false,
	}),
	w200WebP: _getSdnParams({
		width: '200',
		height: '150',
		aspectRatio: '3',
		isWatermark: false,
		isWebP: true,
	}),
	w280: _getSdnParams({
		width: '280',
		height: '280',
		aspectRatio: '3',
		isWatermark: false,
	}),
	w280WebP: _getSdnParams({
		width: '280',
		height: '280',
		aspectRatio: '3',
		isWatermark: false,
		isWebP: true,
	}),
	w280_KEEP: _getSdnParams({ width: '280', height: '280', aspectRatio: '1', isWatermark: false }), // aspectRatio - zachovava rozmery

	w320_WATERMARK_CROP: _getSdnParams({
		width: '320',
		height: '240',
		isCropped: true,
	}),
	w320WebP_WATERMARK_CROP: _getSdnParams({
		width: '320',
		height: '240',
		isWebP: true,
		isCropped: true,
	}),

	w640_WATERMARK_CROP: _getSdnParams({
		width: '640',
		height: '480',
		isCropped: true,
	}),
	w640WebP_WATERMARK_CROP: _getSdnParams({
		width: '640',
		height: '480',
		isCropped: true,
		isWebP: true,
	}),

	w800_WATERMARK_CROP: _getSdnParams({
		width: '800',
		height: '600',
		isCropped: true,
	}),

	w1024_WATERMARK_CROP: _getSdnParams({
		width: '1024',
		height: '768',
		isCropped: true,
	}),

	w320_WATERMARK: _getSdnParams({ width: '320', height: '240' }),
	w320WebP_WATERMARK: _getSdnParams({ width: '320', height: '240', isWebP: true }),
	w640_WATERMARK: _getSdnParams({ width: '640', height: '480' }),
	w640WebP_WATERMARK: _getSdnParams({ width: '640', height: '480', isWebP: true }),
	w800_WATERMARK: _getSdnParams({ width: '800', height: '600' }),
	w800WebP_WATERMARK: _getSdnParams({ width: '800', height: '600', isWebP: true }),
	w1024_WATERMARK: _getSdnParams({ width: '1024', height: '768' }),
	w1024WebP_WATERMARK: _getSdnParams({ width: '1024', height: '768', isWebP: true }),
} as const;

export const SEZNAM_CZ_URL = 'https://www.seznam.cz';

// CSS konstanty jsou nadefinovane v 'shared/styles/variables'
export const DeviceTypes = {
	mobile: {
		name: 'mobile',
	},
	mobileWide: {
		name: 'mobileWide',
		breakpoint: 512,
	},
	tablet: {
		name: 'tablet',
		breakpoint: 768,
	},
	desktop: {
		name: 'desktop',
		breakpoint: 900,
	},
} as const;

export type TDeviceType = keyof typeof DeviceTypes;

export const LOCALITY_TYPES = {
	ADDRESS: 'address',
	DISTRICT: 'district',
	MUNICIPALITY: 'municipality',
	QUARTER: 'quarter',
	REGION: 'region',
	STREET: 'street',
	WARD: 'ward',
} as const;
export type TLocalityTypes = ObjectValues<typeof LOCALITY_TYPES>;

export const LOCALITY_CELA_CR_SEO_NAME = 'cela-cr';
export const LOCALITY_CELA_CR: TLocality = {
	id: 0,
	seoName: LOCALITY_CELA_CR_SEO_NAME,
	name: '',
	type: LOCALITY_TYPES.ADDRESS,
};
export const LOCALITY_CELA_CR_AS_SUGGESTION: LocalitySuggestionData = {
	entityId: 0,
	seoName: LOCALITY_CELA_CR_SEO_NAME,
	suggestFirstRow: '',
	suggestSecondRow: '',
	entityType: LOCALITY_TYPES.ADDRESS,
};

export const COUNTRY_CZECH_ID = 112;

export const KNOWN_LOCALITIES: TLocality[] = [
	{ id: 1, type: LOCALITY_TYPES.REGION, seoName: 'jihocesky', name: 'Jihočeský kraj' },
	{ id: 1, type: LOCALITY_TYPES.DISTRICT, seoName: 'ceske-budejovice', name: 'České Budějovice', isFrequent: true },
	{ id: 2, type: LOCALITY_TYPES.DISTRICT, seoName: 'cesky-krumlov', name: 'Český Krumlov' },
	{ id: 3, type: LOCALITY_TYPES.DISTRICT, seoName: 'jindrichuv-hradec', name: 'Jindřichův Hradec' },
	{ id: 4, type: LOCALITY_TYPES.DISTRICT, seoName: 'pisek', name: 'Písek' },
	{ id: 5, type: LOCALITY_TYPES.DISTRICT, seoName: 'prachatice', name: 'Prachatice' },
	{ id: 6, type: LOCALITY_TYPES.DISTRICT, seoName: 'strakonice', name: 'Strakonice' },
	{ id: 7, type: LOCALITY_TYPES.DISTRICT, seoName: 'tabor', name: 'Tábor' },

	{ id: 14, type: LOCALITY_TYPES.REGION, seoName: 'jihomoravsky', name: 'Jihomoravský kraj' },
	{ id: 71, type: LOCALITY_TYPES.DISTRICT, seoName: 'blansko', name: 'Blansko' },
	{
		id: 72,
		type: LOCALITY_TYPES.DISTRICT,
		seoName: 'brno-mesto',
		name: 'Brno',
		altName: 'Brno-město',
		isFrequent: true,
	},
	{ id: 73, type: LOCALITY_TYPES.DISTRICT, seoName: 'brno-venkov', name: 'Brno-venkov' },
	{ id: 74, type: LOCALITY_TYPES.DISTRICT, seoName: 'breclav', name: 'Břeclav' },
	{ id: 75, type: LOCALITY_TYPES.DISTRICT, seoName: 'hodonin', name: 'Hodonín' },
	{ id: 76, type: LOCALITY_TYPES.DISTRICT, seoName: 'vyskov', name: 'Vyškov' },
	{ id: 77, type: LOCALITY_TYPES.DISTRICT, seoName: 'znojmo', name: 'Znojmo' },

	{ id: 3, type: LOCALITY_TYPES.REGION, seoName: 'karlovarsky', name: 'Karlovarský kraj' },
	{ id: 9, type: LOCALITY_TYPES.DISTRICT, seoName: 'cheb', name: 'Cheb' },
	{ id: 10, type: LOCALITY_TYPES.DISTRICT, seoName: 'karlovy-vary', name: 'Karlovy Vary' },
	{ id: 16, type: LOCALITY_TYPES.DISTRICT, seoName: 'sokolov', name: 'Sokolov' },

	{ id: 6, type: LOCALITY_TYPES.REGION, seoName: 'kralovehradecky', name: 'Královéhradecký kraj' },
	{ id: 28, type: LOCALITY_TYPES.DISTRICT, seoName: 'hradec-kralove', name: 'Hradec Králové', isFrequent: true },
	{ id: 30, type: LOCALITY_TYPES.DISTRICT, seoName: 'jicin', name: 'Jičín' },
	{ id: 31, type: LOCALITY_TYPES.DISTRICT, seoName: 'nachod', name: 'Náchod' },
	{ id: 33, type: LOCALITY_TYPES.DISTRICT, seoName: 'rychnov-nad-kneznou', name: 'Rychnov nad Kněžnou' },
	{ id: 36, type: LOCALITY_TYPES.DISTRICT, seoName: 'trutnov', name: 'Trutnov' },

	{ id: 5, type: LOCALITY_TYPES.REGION, seoName: 'liberecky', name: 'Liberecký kraj' },
	{ id: 18, type: LOCALITY_TYPES.DISTRICT, seoName: 'ceska-lipa', name: 'Česká Lípa' },
	{ id: 21, type: LOCALITY_TYPES.DISTRICT, seoName: 'jablonec-nad-nisou', name: 'Jablonec nad Nisou' },
	{ id: 22, type: LOCALITY_TYPES.DISTRICT, seoName: 'liberec', name: 'Liberec', isFrequent: true },
	{ id: 34, type: LOCALITY_TYPES.DISTRICT, seoName: 'semily', name: 'Semily' },

	{ id: 12, type: LOCALITY_TYPES.REGION, seoName: 'moravskoslezsky', name: 'Moravskoslezský kraj' },
	{ id: 60, type: LOCALITY_TYPES.DISTRICT, seoName: 'bruntal', name: 'Bruntál' },
	{ id: 61, type: LOCALITY_TYPES.DISTRICT, seoName: 'frydek-mistek', name: 'Frýdek-Místek' },
	{ id: 62, type: LOCALITY_TYPES.DISTRICT, seoName: 'karvina', name: 'Karviná' },
	{ id: 63, type: LOCALITY_TYPES.DISTRICT, seoName: 'novy-jicin', name: 'Nový Jičín' },
	{ id: 64, type: LOCALITY_TYPES.DISTRICT, seoName: 'opava', name: 'Opava' },
	{
		id: 65,
		type: LOCALITY_TYPES.DISTRICT,
		seoName: 'ostrava-mesto',
		name: 'Ostrava',
		altName: 'Ostrava-město',
		isFrequent: true,
	},

	{ id: 8, type: LOCALITY_TYPES.REGION, seoName: 'olomoucky', name: 'Olomoucký kraj' },
	{ id: 46, type: LOCALITY_TYPES.DISTRICT, seoName: 'jesenik', name: 'Jeseník' },
	{ id: 42, type: LOCALITY_TYPES.DISTRICT, seoName: 'olomouc', name: 'Olomouc', isFrequent: true },
	{ id: 40, type: LOCALITY_TYPES.DISTRICT, seoName: 'prostejov', name: 'Prostějov' },
	{ id: 43, type: LOCALITY_TYPES.DISTRICT, seoName: 'prerov', name: 'Přerov' },
	{ id: 44, type: LOCALITY_TYPES.DISTRICT, seoName: 'sumperk', name: 'Šumperk' },

	{ id: 7, type: LOCALITY_TYPES.REGION, seoName: 'pardubicky', name: 'Pardubický kraj' },
	{ id: 29, type: LOCALITY_TYPES.DISTRICT, seoName: 'chrudim', name: 'Chrudim' },
	{ id: 32, type: LOCALITY_TYPES.DISTRICT, seoName: 'pardubice', name: 'Pardubice', isFrequent: true },
	{ id: 35, type: LOCALITY_TYPES.DISTRICT, seoName: 'svitavy', name: 'Svitavy' },
	{ id: 37, type: LOCALITY_TYPES.DISTRICT, seoName: 'usti-nad-orlici', name: 'Ústí nad Orlicí' },

	{ id: 2, type: LOCALITY_TYPES.REGION, seoName: 'plzensky', name: 'Plzeňský kraj' },
	{ id: 8, type: LOCALITY_TYPES.DISTRICT, seoName: 'domazlice', name: 'Domažlice' },
	{ id: 11, type: LOCALITY_TYPES.DISTRICT, seoName: 'klatovy', name: 'Klatovy' },
	{ id: 13, type: LOCALITY_TYPES.DISTRICT, seoName: 'plzen-jih', name: 'Plzeň-jih' },
	{
		id: 12,
		type: LOCALITY_TYPES.DISTRICT,
		seoName: 'plzen-mesto',
		name: 'Plzeň',
		altName: 'Plzeň-město',
		isFrequent: true,
	},
	{ id: 14, type: LOCALITY_TYPES.DISTRICT, seoName: 'plzen-sever', name: 'Plzeň-sever' },
	{ id: 15, type: LOCALITY_TYPES.DISTRICT, seoName: 'rokycany', name: 'Rokycany' },
	{ id: 17, type: LOCALITY_TYPES.DISTRICT, seoName: 'tachov', name: 'Tachov' },

	{
		id: 10,
		type: LOCALITY_TYPES.REGION,
		seoName: 'hlavni-mesto-praha',
		name: 'Hlavní město Praha',
	},
	{
		id: 47,
		type: LOCALITY_TYPES.DISTRICT,
		seoName: 'okres-hlavni-mesto-praha',
		name: 'okres Hlavní město Praha',
	},
	{ id: 3468, type: LOCALITY_TYPES.MUNICIPALITY, seoName: 'praha', name: 'Praha', isFrequent: true },
	{ id: 5010, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-10', name: 'Praha 10' },
	{ id: 5001, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-1', name: 'Praha 1' },
	{ id: 5002, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-2', name: 'Praha 2' },
	{ id: 5003, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-3', name: 'Praha 3' },
	{ id: 5004, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-4', name: 'Praha 4' },
	{ id: 5005, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-5', name: 'Praha 5' },
	{ id: 5006, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-6', name: 'Praha 6' },
	{ id: 5007, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-7', name: 'Praha 7' },
	{ id: 5008, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-8', name: 'Praha 8' },
	{ id: 5009, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-9', name: 'Praha 9' },

	{ id: 11, type: LOCALITY_TYPES.REGION, seoName: 'stredocesky', name: 'Středočeský kraj' },
	{ id: 48, type: LOCALITY_TYPES.DISTRICT, seoName: 'benesov', name: 'Benešov' },
	{ id: 49, type: LOCALITY_TYPES.DISTRICT, seoName: 'beroun', name: 'Beroun' },
	{ id: 50, type: LOCALITY_TYPES.DISTRICT, seoName: 'kladno', name: 'Kladno' },
	{ id: 51, type: LOCALITY_TYPES.DISTRICT, seoName: 'kolin', name: 'Kolín' },
	{ id: 52, type: LOCALITY_TYPES.DISTRICT, seoName: 'kutna-hora', name: 'Kutná Hora' },
	{ id: 54, type: LOCALITY_TYPES.DISTRICT, seoName: 'melnik', name: 'Mělník' },
	{ id: 53, type: LOCALITY_TYPES.DISTRICT, seoName: 'mlada-boleslav', name: 'Mladá Boleslav' },
	{ id: 55, type: LOCALITY_TYPES.DISTRICT, seoName: 'nymburk', name: 'Nymburk' },
	{ id: 56, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-vychod', name: 'Praha-východ' },
	{ id: 57, type: LOCALITY_TYPES.DISTRICT, seoName: 'praha-zapad', name: 'Praha-západ' },
	{ id: 58, type: LOCALITY_TYPES.DISTRICT, seoName: 'pribram', name: 'Příbram' },
	{ id: 59, type: LOCALITY_TYPES.DISTRICT, seoName: 'rakovnik', name: 'Rakovník' },

	{ id: 4, type: LOCALITY_TYPES.REGION, seoName: 'ustecky', name: 'Ústecký kraj' },
	{ id: 19, type: LOCALITY_TYPES.DISTRICT, seoName: 'decin', name: 'Děčín' },
	{ id: 20, type: LOCALITY_TYPES.DISTRICT, seoName: 'chomutov', name: 'Chomutov' },
	{ id: 23, type: LOCALITY_TYPES.DISTRICT, seoName: 'litomerice', name: 'Litoměřice' },
	{ id: 24, type: LOCALITY_TYPES.DISTRICT, seoName: 'louny', name: 'Louny' },
	{ id: 25, type: LOCALITY_TYPES.DISTRICT, seoName: 'most', name: 'Most' },
	{ id: 26, type: LOCALITY_TYPES.DISTRICT, seoName: 'teplice', name: 'Teplice' },
	{ id: 27, type: LOCALITY_TYPES.DISTRICT, seoName: 'usti-nad-labem', name: 'Ústí nad Labem', isFrequent: true },

	{ id: 13, type: LOCALITY_TYPES.REGION, seoName: 'vysocina', name: 'Kraj Vysočina' },
	{ id: 66, type: LOCALITY_TYPES.DISTRICT, seoName: 'havlickuv-brod', name: 'Havlíčkův Brod' },
	{ id: 67, type: LOCALITY_TYPES.DISTRICT, seoName: 'jihlava', name: 'Jihlava' },
	{ id: 68, type: LOCALITY_TYPES.DISTRICT, seoName: 'pelhrimov', name: 'Pelhřimov' },
	{ id: 69, type: LOCALITY_TYPES.DISTRICT, seoName: 'trebic', name: 'Třebíč' },
	{ id: 70, type: LOCALITY_TYPES.DISTRICT, seoName: 'zdar-nad-sazavou', name: 'Žďár nad Sázavou' },

	{ id: 9, type: LOCALITY_TYPES.REGION, seoName: 'zlinsky', name: 'Zlínský kraj' },
	{ id: 39, type: LOCALITY_TYPES.DISTRICT, seoName: 'kromeriz', name: 'Kroměříž' },
	{ id: 41, type: LOCALITY_TYPES.DISTRICT, seoName: 'uherske-hradiste', name: 'Uherské Hradiště' },
	{ id: 45, type: LOCALITY_TYPES.DISTRICT, seoName: 'vsetin', name: 'Vsetín' },
	{ id: 38, type: LOCALITY_TYPES.DISTRICT, seoName: 'zlin', name: 'Zlín' },
] as const;

function getKnownLocalitiesIndex(locality: LocalityData | TLocality | LocalitySuggestionData) {
	if ('entityId' in locality) {
		return `${locality.entityId}-${locality.entityType}`;
	}

	return `${locality.id}-${locality.type}`;
}
const KNOWN_LOCALITIES_INDEXED = new Map(
	KNOWN_LOCALITIES.map((locality) => [getKnownLocalitiesIndex(locality), locality]),
);
export function getKnownLocalityByLocality(
	locality: LocalityData | TLocality | LocalitySuggestionData,
): TLocality | undefined {
	return KNOWN_LOCALITIES_INDEXED.get(getKnownLocalitiesIndex(locality));
}

export type TLocality = {
	altName?: string | undefined;
	isFrequent?: boolean | undefined;
	id: number;
	type: TLocalityTypes;
	seoName: string;
	name: string;
};

export const SORT_BY_KEYS = ['NEWEST', 'PRICE_DESC', 'PRICE_ASC'] as const;
export type TSortByKeys = (typeof SORT_BY_KEYS)[number];
export const SORT_BY_SEO_NAMES = {
	NEWEST: 'nejnovejsi',
	PRICE_ASC: 'nejlevnejsi',
	PRICE_DESC: 'nejdrazsi',
} as const;
export const SORT_SEO_NAMES_VALUES = Object.values(SORT_BY_SEO_NAMES);
export type TSortBySeoNames = ObjectValues<typeof SORT_BY_SEO_NAMES>;
export const SORT_BY_SORT = {
	NEWEST: '-create_date',
	PRICE_DESC: '-price',
	PRICE_ASC: 'price',
} as const;
export type TSortBySort = ObjectValues<typeof SORT_BY_SORT>;
export type TSortKeysBySeoName = Record<TSortBySeoNames, TSortByKeys>;
export const SORT_KEYS_BY_SEO_NAME: TSortKeysBySeoName = {
	nejnovejsi: 'NEWEST',
	nejdrazsi: 'PRICE_DESC',
	nejlevnejsi: 'PRICE_ASC',
} as const;
export type TSortKeysBySort = Record<TSortBySort, TSortByKeys>;
export const SORT_KEYS_BY_SORT = {
	'-create_date': 'NEWEST',
	'-price': 'PRICE_DESC',
	price: 'PRICE_ASC',
} as const;

export const PRICE = {
	FREE: 'zdarma',
	NO_LIMIT: 'cena-neomezena',
	TO: 'cena-do',
	FROM: 'cena-od',
	BETWEEN: 'cena-od-do',
} as const;
export type TPrice = ObjectValues<typeof PRICE>;

export const DEFAULT_RADIUS_VALUE = 5;

export const PaymentType = {
	CARD: 'credit_card',
	SMS: 'sms',
	WALLET: 'wallet',
	VOUCHER: 'voucher',
} as const;

export const PaymentStatus = {
	PROCESSED: 'processed',
	FAILED: 'failed',
} as const;

export const HP_LOGO_ELEMENT_ID = 'hp-logo';

export const HEADER_DATA_ELEMENT_ID = 'headerData';

export const ANIMAL_CATEGORY_WARNING_URL =
	'https://napoveda.seznam.cz/cz/sbazar/prodej-zvirat-pres-internet/na-co-si-dat-pozor-pri-koupi-zvirete/?utm_source=sbazar-promo&utm_medium=link&utm_campaign=mnozirny';
